import React, {useContext} from 'react'
import {useNavigate} from 'react-router'
import {AppHeader} from './app-shell/AppHeader'
import {HeaderLogo} from './app-shell/AppHeader.styles'
import {useLocation} from 'react-router-dom'
import {useDispatch, useSelector} from 'react-redux'
import {
  selectEffectiveRole,
  selectPartnerAC,
  selectEaid,
} from '../app-store/selector'
import {headerContext} from '../context/header-context'
import {
  setKeapPartnerId,
  setBundleId,
  setEaid,
} from '../../src/app-store/userSlice'
import {setLinkType} from '../app-store/kpGlobalStateSlice'
import {
  resetCatalogCardCollapseStatus,
  resetParentCardCollapseStatus,
  resetToDefaultCart,
} from '../app-store/kp11CartSlice'
import {resetCouponData} from '../app-store/kp11CouponSlice'
import {useAsync} from '../utils/use-async'
import {useAuthClient} from '../utils/use-auth-client'
import {kP11LinkService} from './keap-partner/kp-11-link/kp-11Link-utility/kp11-service'
import {Modal} from '@thryvlabs/maverick'
import thryvLogo from '../core/assets/sideNav/THRY_BIG.svg'

function HeaderKeapPartner({
  userData,
  isSidebarOpen,
  isMobileSidebarOpen,
  setIsMobileSidebarOpen,
}) {
  const dispatch = useDispatch()
  const keapPartnerId = useSelector(state => state.keapPartner?.keapPartnerId)
  const location = useLocation()
  // const urlParams = new URLSearchParams(location.search)
  // const keapPartnerId = urlParams?.get('pac')
  const {isHeaderVisible} = useContext(headerContext)
  const navigate = useNavigate()
  const client = useAuthClient()
  const {run} = useAsync({status: 'pending'})
  const [slpEnabled, setSlpEnabled] = React.useState(null)
  const [showSlpBlockedModal, setShowSlpBlockedModal] = React.useState(false)
  const [showEsbErrorModal, setShowEsbErrorModal] = React.useState(false)
  const [showNoEaidModal, setShowNoEaidModal] = React.useState(false)

  React.useEffect(() => {
    run(client('keappartner/fetchConfigLookupValue/slp_integration_enabled'))
      .then(resp => {
        setSlpEnabled(resp?.data?.[0]?.value === 'true')
      })
      .catch(() => setSlpEnabled(false))
  }, [client, run])

  React.useEffect(() => {
    if (slpEnabled === null) return // wait until flag is loaded
    // Always check URL first
    const urlParams = new URLSearchParams(location.search)
    const urlPartnerId = urlParams?.get('pac')
    const storedPartnerId = sessionStorage.getItem('keapPartnerId')
    // If URL has pac parameter, use it (takes priority)
    if (urlPartnerId) {
      if (urlPartnerId !== storedPartnerId) {
        sessionStorage.setItem('keapPartnerId', urlPartnerId)
      }
      dispatch(setKeapPartnerId(urlPartnerId))
    } else if (storedPartnerId) {
      // If no URL parameter but we have stored value, use stored
      dispatch(setKeapPartnerId(storedPartnerId))
    }

    const urlBundleId = urlParams?.get('slp_share_link_id')

    if (urlBundleId && !slpEnabled) {
      setShowSlpBlockedModal(true)
      return
    }

    const storedBundleId = sessionStorage.getItem('bundleId')
    if (urlBundleId) {
      if (urlBundleId !== storedBundleId) {
        sessionStorage.setItem('bundleId', urlBundleId)
      }
      dispatch(setBundleId(urlBundleId))
    } else if (storedBundleId) {
      dispatch(setBundleId(storedBundleId))
    }

    // EAID is no longer read from URL; it is fetched via API when bundleId is present.
    // On page loads without bundleId in URL, restore eaid from sessionStorage.
    if (!urlBundleId) {
      const storedEaid = sessionStorage.getItem('eaid')
      if (storedEaid) {
        dispatch(setEaid(storedEaid))
      }
    }
  }, [dispatch, location.search, slpEnabled])

  React.useEffect(() => {
    if (slpEnabled === null || !userData?.email) return
    const urlParams = new URLSearchParams(location.search)
    const urlBundleId = urlParams?.get('slp_share_link_id')
    if (!slpEnabled || !urlBundleId) return

    let cancelled = false
    run(
      kP11LinkService.fetchPartnerEaidByEmail(
        client,
        userData.user_metadata?.networkId,
      ),
    )
      .then(data => {
        if (cancelled) return
        const eaidValue = data?.data?.[0]?.Contact?.Account?.SV_Account_Id__c
        if (eaidValue) {
          dispatch(setEaid(eaidValue))
        } else {
          setShowNoEaidModal(true)
        }
      })
      .catch(() => {
        if (!cancelled) setShowNoEaidModal(true)
      })
    return () => {
      cancelled = true
    }
  }, [slpEnabled, userData, location.search, dispatch, client, run])

  // Get partnerAffiliateCode from Redux (set when user switches to KeapPartner role)
  const partnerAffiliateCodeFromRedux = useSelector(selectPartnerAC)
  const eaid = useSelector(selectEaid)

  // const {isLoading, isAuthenticated, error, user} = useSelector(selectUserData)
  const userRoles = useSelector(selectEffectiveRole)

  const isInfluencerOps =
    Array.isArray(userRoles) && userRoles.includes('InfluencerOps')

  const isKeapPartner =
    Array.isArray(userRoles) && userRoles.includes('KeapPartner')

  // Priority: Redux value > URL param
  const keapPartnerIdValue = partnerAffiliateCodeFromRedux || keapPartnerId

  React.useEffect(() => {
    // Only set if not already in Redux
    if (!keapPartnerId) {
      const urlParams = new URLSearchParams(location.search)
      const keapPartnerId = urlParams?.get('pac')

      if (keapPartnerId) {
        dispatch(setKeapPartnerId(keapPartnerId))
      }
    }
  }, [dispatch, keapPartnerId, location.search])

  React.useEffect(() => {
    let cancelled = false
    const urlParams = new URLSearchParams(location.search)
    const hasPac = !!urlParams?.get('pac')
    if (!eaid || hasPac || !slpEnabled) return
    run(kP11LinkService.fetchPartnerAffiliateCodeByEaid(client, eaid))
      .then(data => {
        if (cancelled) return
        if (data?.partnerAffiliateCode) {
          dispatch(setKeapPartnerId(data.partnerAffiliateCode))
        } else {
          setShowEsbErrorModal(true)
        }
      })
      .catch(() => {
        if (!cancelled) setShowEsbErrorModal(true)
      })
    return () => {
      cancelled = true
    }
  }, [eaid, dispatch, client, run, location.search, slpEnabled])

  const resetSlicersState = () => {
    dispatch(resetToDefaultCart())
    dispatch(resetCouponData())
    dispatch(resetParentCardCollapseStatus())
    dispatch(resetCatalogCardCollapseStatus())
  }

  const handleManyPurchaseClick = () => {
    dispatch(setLinkType('1:many'))
    resetSlicersState()
    // The 1:Many catalog has its own country dropdown (US by default, except
    // Keap partners who must pick one), so every role goes straight to it —
    // no separate country-selection page.
    navigate('/keap-partner-catalog', {
      state: {
        linkType: '1:many',
        selectedCountry: isKeapPartner ? '' : 'US',
        keapPartnerId:
          isInfluencerOps || isKeapPartner ? keapPartnerIdValue : keapPartnerId,
      },
    })
  }

  // Opens the full side nav (shown on every non-landing route) with Account
  // Management selected, mirroring the side-nav items' navigation state.
  const handleMyClientCenterClick = () => {
    dispatch(setLinkType('1:1'))
    resetSlicersState()
    navigate('/control-center', {
      state: {
        linkType: '1:1',
        userData,
        keapPartnerId,
        activePanel: 'Account Management',
      },
    })
  }

  // Landing has no side nav, so the brand logo moves into the header.
  const isLanding = location.pathname === '/'

  if (!isHeaderVisible) {
    return null
  }

  if (showEsbErrorModal) {
    return (
      <Modal
        altBtn={<></>}
        openOnLoad
        variant="default"
        hideX
        disableX
        disableBackdropClick
        footer={false}
      >
        <div style={{padding: '20px', textAlign: 'center'}}>
          <h3
            style={{
              color: '#FF5000',
              marginBottom: '12px',
              fontSize: '20px',
              fontWeight: '600',
            }}
          >
            Partner Affiliate Code Not Found
          </h3>
          <p
            style={{
              marginTop: '16px',
              marginBottom: '0',
              color: '#333',
              fontSize: '14px',
              lineHeight: '1.6',
            }}
          >
            Keap Partner Affiliate Code not found for the given EAID. You cannot
            proceed with ordering.
          </p>
        </div>
      </Modal>
    )
  }

  if (showNoEaidModal) {
    return (
      <Modal
        altBtn={<></>}
        openOnLoad
        variant="default"
        hideX
        disableX
        disableBackdropClick
        footer={false}
      >
        <div style={{padding: '20px', textAlign: 'center'}}>
          <h3
            style={{
              color: '#FF5000',
              marginBottom: '12px',
              fontSize: '20px',
              fontWeight: '600',
            }}
          >
            EAID Not Found
          </h3>
          <p
            style={{
              marginTop: '16px',
              marginBottom: '0',
              color: '#333',
              fontSize: '14px',
              lineHeight: '1.6',
            }}
          >
            No EAID could be retrieved for the partner. You cannot proceed with
            ordering.
          </p>
        </div>
      </Modal>
    )
  }

  if (showSlpBlockedModal) {
    return (
      <Modal
        altBtn={<></>}
        openOnLoad
        variant="default"
        hideX
        disableX
        disableBackdropClick
        footer={false}
      >
        <div style={{padding: '20px', textAlign: 'center'}}>
          <h3
            style={{
              color: '#FF5000',
              marginBottom: '12px',
              fontSize: '20px',
              fontWeight: '600',
            }}
          >
            Access Denied
          </h3>
          <p
            style={{
              marginTop: '16px',
              marginBottom: '0',
              color: '#333',
              fontSize: '14px',
              lineHeight: '1.6',
            }}
          >
            SLP integration is not enabled. You cannot proceed with this link.
          </p>
        </div>
      </Modal>
    )
  }

  return (
    <AppHeader
      isSidebarOpen={isSidebarOpen}
      isMobileSidebarOpen={isMobileSidebarOpen}
      setIsMobileSidebarOpen={setIsMobileSidebarOpen}
      fullWidth={isLanding}
      blurOnScroll={isLanding}
      logo={
        isLanding ? (
          <HeaderLogo>
            <img src={thryvLogo} alt="Thryv" />
          </HeaderLogo>
        ) : undefined
      }
      actions={[
        {
          key: 'my-client-center',
          text: 'My Client Center',
          plain: true,
          onClick: handleMyClientCenterClick,
        },
        {
          key: 'many-purchase',
          text: '1:Many Purchase Link',
          onClick: handleManyPurchaseClick,
        },
      ]}
    />
  )
}

export {HeaderKeapPartner}
